import { CheckCircle2, CircleAlert, Clock3, RotateCw, X } from 'lucide-react';
import { Pill } from '@/shared/components';
import type { AriOperationState } from '@/shared/api';
import {
  ariOperationDescription,
  ariOperationLabel,
  shouldDisplayAriOperation,
} from './ariOperationStatus';
import { useAriOperationStatuses } from './useAriOperationStatuses';

export function AriOperationStatusPanel({ propertyId }: { propertyId: string }) {
  const { tracked, items, isLoading, isError, dismiss } = useAriOperationStatuses(propertyId);
  if (tracked.length === 0) return null;
  const byId = new Map(items.map((item) => [item.operationId, item]));
  const visibleTracked = tracked.filter((trackedItem) =>
    shouldDisplayAriOperation(byId.get(trackedItem.id)?.status ?? 'Queued'),
  );
  if (visibleTracked.length === 0) return null;

  return (
    <section
      className="bg-card border-line/50 shadow-card rounded-lg border p-4"
      aria-live="polite"
    >
      <div className="mb-3">
        <div className="text-ink-soft text-[11px] font-semibold uppercase tracking-[0.12em]">
          Laufende Übertragungen
        </div>
        <p className="text-ink-faint mt-1 text-xs">
          Der Status kommt direkt aus Warteschlange und Channex-Rückprüfung und bleibt nach einem
          Neuladen erhalten.
        </p>
      </div>
      {isError && <p className="text-sealwax text-xs">Status konnte nicht geladen werden.</p>}
      <ul className="divide-line/50 divide-y">
        {visibleTracked.map((trackedItem) => {
          const item = byId.get(trackedItem.id);
          const status = item?.status ?? 'Queued';
          return (
            <li key={trackedItem.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
              <StatusIcon status={status} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill variant={tone(status)} size="sm">
                    {isLoading && !item ? 'Status wird geladen' : ariOperationLabel(status)}
                  </Pill>
                  {item && item.attempts > 1 && (
                    <span className="text-ink-faint text-[11px]">{item.attempts} Versuche</span>
                  )}
                </div>
                <p className="text-ink-soft mt-1 text-xs">
                  {item?.message || ariOperationDescription(status)}
                </p>
                {item?.readback?.driftSummary.map((detail) => (
                  <p key={detail} className="text-amber mt-1 text-[11px]">
                    {detail}
                  </p>
                ))}
              </div>
              {item && status !== 'Queued' && status !== 'Retrying' && (
                <button
                  type="button"
                  className="text-ink-faint hover:bg-card-alt hover:text-ink focus-visible:ring-sage flex size-11 shrink-0 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2"
                  aria-label="Status ausblenden"
                  onClick={() => dismiss(item.operationId)}
                >
                  <X size={14} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function StatusIcon({ status }: { status: AriOperationState }) {
  const className =
    status === 'Confirmed' ? 'text-sage' : status === 'Queued' ? 'text-ink-faint' : 'text-amber';
  if (status === 'Confirmed') return <CheckCircle2 className={className} size={17} />;
  if (status === 'Queued') return <Clock3 className={className} size={17} />;
  if (status === 'Retrying') return <RotateCw className={className} size={17} />;
  return <CircleAlert className={className} size={17} />;
}

function tone(status: AriOperationState): 'live' | 'warning' | 'stop' | 'default' {
  if (status === 'Confirmed') return 'live';
  if (status === 'Failed' || status === 'Suppressed') return 'stop';
  if (status === 'Queued') return 'default';
  return 'warning';
}
